import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import AuthCard from "@/components/AuthCard";
import { useAuth } from "@/contexts/AuthContext";

const HERO_IMG =
  "https://static.prod-images.emergentagent.com/jobs/b4039025-8e24-4c65-a982-2783f61534fb/images/d38b107abcd7de59c9aa78ea8ed58c4b3b3457344c934f3b392e33b755f2c21d.png";

export default function Landing() {
  const navigate = useNavigate();
  const { user, bootstrapped } = useAuth();

  // If already authenticated, send straight to /app
  useEffect(() => {
    if (bootstrapped && user) navigate("/app", { replace: true });
  }, [bootstrapped, user, navigate]);

  return (
    <div
      data-testid="landing-page"
      className="flex flex-col min-h-screen w-full bg-black overflow-x-hidden"
    >
      {/* ─── TOP HALF: HERO ─── */}
      <section
        data-testid="hero-section"
        className="relative w-full flex-shrink-0 h-[45vh] sm:h-[55vh] lg:h-[60vh] bg-black"
      >
        <img
          src={HERO_IMG}
          alt="A golden 3D soccer ball floating in a starry dark cosmos"
          data-testid="hero-image"
          className="absolute inset-0 w-full h-full object-cover object-center"
          draggable={false}
        />
        {/* Vignette: blend hero into pure-black bottom half */}
        <div className="absolute inset-0 hero-vignette pointer-events-none" />

        {/* Wordmark */}
        <header className="absolute top-0 left-0 w-full px-6 sm:px-10 py-5 z-10 flex items-center">
          <span
            data-testid="figumatch-wordmark"
            className="figumatch-wordmark text-base sm:text-lg font-semibold"
          >
            FiguMatch
          </span>
        </header>
      </section>

      {/* ─── BOTTOM HALF: TEXT + AUTH CARD ─── */}
      <section
        data-testid="bottom-section"
        className="relative flex-1 bg-black flex flex-col items-center justify-start px-5 pt-10 sm:pt-14 pb-20"
      >
        <div className="w-full max-w-[520px] mx-auto flex flex-col items-center text-center">
          <h1
            data-testid="landing-h1"
            className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white"
          >
            Trade like a pro.
          </h1>
          <p
            data-testid="landing-subtitle"
            className="font-body text-base sm:text-lg text-[#A1A1AA] font-light mt-4 max-w-md"
          >
            Find nearby collectors. Snap up the numbers you need. Finish the album.
          </p>

          <div className="w-full flex justify-center mt-10">
            <AuthCard defaultTab="login" />
          </div>

          {/* Legal footer — post-AuthCard, tracked via /api/legal/track-open */}
          <div className="mt-8 flex justify-center gap-6 text-xs text-neutral-500 font-body">
            <a
              data-testid="legal-link-terms-landing"
              href={`${process.env.REACT_APP_BACKEND_URL}/api/legal/track-open?document_type=terms`}
              className="hover:text-amber-400 transition-colors"
            >
              Terms of Service
            </a>
            <span className="text-neutral-700" aria-hidden="true">
              ·
            </span>
            <a
              data-testid="legal-link-privacy-landing"
              href={`${process.env.REACT_APP_BACKEND_URL}/api/legal/track-open?document_type=privacy`}
              className="hover:text-amber-400 transition-colors"
            >
              Privacy Policy
            </a>
          </div>

          <p
            data-testid="landing-footnote"
            className="font-body text-xs text-[#A1A1AA]/60 mt-10 tracking-wide"
          >
            © {new Date().getFullYear()} FiguMatch · Premium sticker trading for collectors
          </p>
        </div>
      </section>
    </div>
  );
}
